import React, { useEffect, useState } from 'react'
import ReactEchartsCore from 'echarts-for-react/lib/core'

import echarts from 'echarts/lib/echarts'
import 'echarts/lib/chart/tree'
import 'echarts/lib/chart/line'
import 'echarts/lib/chart/lines'
import 'echarts/lib/chart/bar'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/dataZoom'
import 'echarts/lib/component/brush'
import 'echarts/lib/component/toolbox'
import 'echarts/lib/component/title'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/graphic'
import { IBarChartProps } from '../utils'
import { useTheme } from '@mui/material'
import '../styles.css'
import moment from 'moment'
import { DEV_COLOR, PROD_COLOR, TEST_COLOR } from '../../../assets/jss/material-dashboard-react'
import { formatNumber } from '../../../utils/utils'

const BarChart = (props: IBarChartProps) => {
  const { name, yAxisTitle, seriesData, frequency, loading, timeRange } = { ...props }

  const theme = useTheme()
  const [series, setSeries] = useState<any>()
  const [noData, setNoData] = useState(false)

  useEffect(() => {
    if (seriesData.length == 0) {
      setNoData(true)
    } else {
      setNoData(false)
    }
  }, [seriesData])

  useEffect(() => {
    if (Array.isArray(seriesData)) {
    } else {
      let data = [] as any
      if (Object.keys(seriesData).length == 0) {
        setNoData(true)
        setSeries({
          name: name,
          data: [],
          type: 'line',
          symbol: 'circle',
          lineStyle: {
            width: 2.5,
          },
        })
      } else {
        setNoData(false)
        Object.keys(seriesData).forEach((key) => {
          data.push({
            name: key,
            data: seriesData[key],
            type: 'bar',
            stack: 'total',
            barWidth: '50%',
            itemStyle: {
              color: key == 'dev' ? DEV_COLOR : key == 'test' ? TEST_COLOR : key == 'prod' ? PROD_COLOR : '',
              borderRadius: key === 'prod' ? [8, 8, 0, 0] : 0,
            },
            emphasis: {
              focus: 'series',
            },
          })
        })
      }
      setSeries(data)
    }
  }, [seriesData])

  return (
    <ReactEchartsCore
      style={{ height: '100%' }}
      echarts={echarts}
      showLoading={loading}
      loadingOption={{
        maskColor: theme.palette.mode == 'dark' ? 'rgb(0,0,0,0.8)' : 'rgb(255,255,255,0.8)',
        textColor: theme.palette.mode == 'dark' ? 'white' : 'black',
      }}
      option={{
        grid: { top: 40, bottom: 0, right: 40, left: 50, height: '83%', containLabel: true },
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          invisible: !noData,
          style: {
            text: 'No data available',
            fill: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.5)',
            fontSize: 14,
            fontWeight: 500,
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(23, 23, 23, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(115, 115, 115, 0.3)' : 'rgba(0, 0, 0, 0.1)',
          borderWidth: 1,
          textStyle: {
            color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            fontSize: 12,
          },
          padding: 12,
          formatter: (params) => {
            let date = moment.utc(params[0].data[0]).format('MM/DD')
            let devVal: any = params[0].data[1]
            let testVal: any = params[1].data[1]
            let prodVal: any = params[2].data[1]
            if (name == 'Cost') {
              devVal = devVal != null ? '$' + formatNumber(devVal.toFixed(2)) : '-'
              testVal = testVal != null ? '$' + formatNumber(testVal.toFixed(2)) : '-'
              prodVal = prodVal != null ? '$' + formatNumber(prodVal.toFixed(2)) : '-'
            } else {
              devVal = typeof devVal === 'number' ? formatNumber(devVal.toFixed(2)) : '-'
              testVal = typeof testVal === 'number' ? formatNumber(testVal.toFixed(2)) : '-'
              prodVal = typeof prodVal === 'number' ? formatNumber(prodVal.toFixed(2)) : '-'
            }
            return `${date}<br />${params[2].marker}Prod: ${prodVal}<br />${params[1].marker}Test: ${testVal}<br />${params[0].marker}Dev: ${devVal}`
          },
        },
        xAxis: {
          type: 'category',
          splitLine: {
            show: false,
          },
          axisLabel: {
            color: theme.palette.mode == 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            fontSize: 11,
            fontWeight: 500,
            formatter: function (value: string) {
              if (frequency == 'hourly') {
                return moment(value).format('HH:mm')
              } else {
                return moment.utc(value).format('MM/DD')
              }
            },
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: theme.palette.mode == 'dark' ? 'rgba(64, 64, 64, 0.4)' : 'rgba(0, 0, 0, 0.1)',
              width: 1,
            },
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: yAxisTitle,
          nameLocation: 'middle',
          nameGap: 50,
          nameTextStyle: {
            color: theme.palette.mode == 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            fontSize: 12,
            fontWeight: 500,
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: theme.palette.mode == 'dark' ? 'rgba(64, 64, 64, 0.2)' : 'rgba(0, 0, 0, 0.06)',
              type: 'dashed',
            },
          },
          axisLabel: {
            color: theme.palette.mode == 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            fontSize: 11,
            fontWeight: 500,
          },
          axisLine: {
            show: true,
          },
          axisTick: {
            show: false,
          },
        },
        series: series,
      }}
    />
  )
}

export default BarChart
